import api from '../api';

export type SettingScope = 'GLOBAL' | 'EDITION' | 'EVENT';
export type SettingType = 'STRING' | 'NUMBER' | 'BOOLEAN' | 'JSON' | 'DATETIME';

export interface SystemSetting {
  id: string;
  scope: SettingScope;
  editionId: string | null;
  eventId: string | null;
  key: string;
  value: unknown;
  type: SettingType;
  description: string | null;
  updatedBy: string;
  updatedAt: string;
}

export interface GetSettingsParams {
  scope?: SettingScope;
  eventId?: string;
  editionId?: string;
  category?: string;
}

export interface UpdateSettingPayload {
  scope: SettingScope;
  editionId?: string | null;
  eventId?: string | null;
  value: unknown;
  type: SettingType;
  description?: string;
}

export const settingsApi = {
  getSettings: async (params?: GetSettingsParams): Promise<SystemSetting[]> => {
    const sanitizedParams = params
      ? Object.fromEntries(
          Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
        )
      : undefined;

    const { data } = await api.get('/settings', { params: sanitizedParams });
    return data;
  },

  getSetting: async (key: string, params?: Omit<GetSettingsParams, 'category'>): Promise<SystemSetting> => {
    const { data } = await api.get(`/settings/${encodeURIComponent(key)}`, { params });
    return data;
  },

  updateSetting: async (key: string, payload: UpdateSettingPayload): Promise<SystemSetting> => {
    const { data } = await api.patch(`/settings/${encodeURIComponent(key)}`, payload);
    return data;
  },

  deleteSetting: async (
    key: string,
    payload: { scope: SettingScope; eventId?: string | null; editionId?: string | null }
  ): Promise<{ message: string }> => {
    const { data } = await api.delete(`/settings/${encodeURIComponent(key)}`, { data: payload });
    return data;
  },

  getPublicSettings: async (eventId?: string): Promise<Record<string, unknown>> => {
    const { data } = await api.get('/public/settings', {
      params: eventId ? { eventId } : undefined,
    });
    return data;
  },

  flushCache: async (): Promise<{ message: string; deletedKeys: number }> => {
    const { data } = await api.post('/admin/settings/cache-flush');
    return data;
  },
};

export const getPublicSettings = settingsApi.getPublicSettings;
